import moment from 'moment';

/**
 * What the account summary shows, worked out from what the site already holds: the account's own
 * balances, the bank transactions the dashboard loaded, the import rules and their account maps, and
 * the budget's categories. Nothing here asks the server for anything.
 */

/** How many transactions the summary lists before offering the rest. */
export const SUMMARY_TRANSACTION_LIMIT = 10;

/** A history shorter than this is drawn a day at a time; anything longer, a month at a time. */
const DAILY_SPAN_DAYS = 92;

/**
 * The account's bank transactions among those already loaded (the dashboard's, in the transaction
 * finder), newest first, in the shape account/transactions answers with so the same list can draw
 * either: `{ count, first, last, transactions }`, dates as YYYY-MM-DD and amounts in cents signed the
 * way a statement reads, money in positive. Loaded rows are in Plaid's sign, money out positive.
 *
 * Null when none of the loaded rows are this account's.
 */
export function loadedAccountTransactions(bankTransactions, accountId, limit = SUMMARY_TRANSACTION_LIMIT) {
	const mine = (bankTransactions || []).filter((transaction) => {
		return transaction && transaction.account && transaction.account.id === accountId && transaction.date;
	});
	if(!mine.length) {
		return null;
	}
	mine.sort((a, b) => b.date.valueOf() - a.date.valueOf());
	const day = (transaction) => transaction.date.format('YYYY-MM-DD');
	return {
		count: mine.length,
		first: day(mine[mine.length - 1]),
		last: day(mine[0]),
		transactions: mine.slice(0, limit).map((transaction) => ({
			id: transaction.id || transaction.plaid_transaction_id,
			date: day(transaction),
			name: transaction.name,
			amount: transaction.amount === null || transaction.amount === undefined ? null : -transaction.amount,
			pending: !!transaction.pending,
		})),
	};
}

/**
 * The names an import knows this account by. A rule names an account by the import's own label for
 * it ("IND 2022 CHEVROLET BOLT EUV (...0002)"), which is rarely the name the account has here, so
 * the account maps that send each label to an account are what tie the two together.
 */
export function importNamesForAccount(account, accountRules) {
	const names = new Set();
	for(const map of accountRules || []) {
		if(map.action === 'assign' && map.plaid_account_id !== null && Number(map.plaid_account_id) === Number(account.id)) {
			names.add(map.external_key);
		}
	}
	return names;
}

/** The rules that name one of `names` as their account, in the order they are tried. */
export function rulesForAccount(rules, names) {
	if(!names || names.size === 0) {
		return [];
	}
	return (rules || []).filter((rule) => !!rule.external_account && names.has(rule.external_account));
}

/**
 * The loan categories the account's rules file rows into. A loan's own account carries its payments,
 * and the rule that files them names the budget category the loan is tracked under, which is the
 * only link between the two. `categories` is the dashboard's Map of them by id. A rule whose
 * category has been deleted links nothing.
 */
export function loanCategoriesForRules(rules, categories) {
	if(!categories || typeof categories.get !== 'function') {
		return [];
	}
	const found = [];
	for(const rule of rules || []) {
		if(rule.action !== 'assign' || !rule.category_id || rule.category_deleted) {
			continue;
		}
		const category = categories.get(rule.category_id) || categories.get(Number(rule.category_id));
		if(category && typeof category.isLoanCategory === 'function' && category.isLoanCategory() && !found.includes(category)) {
			found.push(category);
		}
	}
	return found;
}

/**
 * The loan categories an account is the loan of. Only a debt can be: checking pays a loan too, and
 * its "Loan Repayment" rule files into the loan's category, but that makes it the loan's payer, not
 * the loan.
 *
 * The category chosen in the account's editor (`loan_category_id`) is the answer when there is one,
 * and the rules are read only when there is none. A chosen category that is not among `categories`,
 * or has been deleted, links nothing; the rules do not stand in for it.
 */
export function loanCategoriesForAccount(account, rules, categories) {
	if(!account.isLiability()) {
		return [];
	}
	const chosen = account.loan_category_id;
	if(chosen === null || chosen === undefined || chosen === '') {
		return loanCategoriesForRules(rules, categories);
	}
	if(!categories || typeof categories.get !== 'function') {
		return [];
	}
	const category = categories.get(chosen) || categories.get(Number(chosen)) || categories.get(String(chosen));
	return category && !category.deleted ? [category] : [];
}

/**
 * What a balance is drawn as: what is owed on a debt, so paying it down reads as a falling line, and
 * what is held on anything else.
 */
function plottedAmount(account, balance) {
	const relative = balance.getRelativeBalanceAmount();
	return account.isLiability() ? -relative : relative;
}

/**
 * A loan category's recorded balances less the zeros some later month contradicts: a month whose
 * budget was saved with its balance left at 0 is a month nobody recorded, not one where the loan was
 * paid off and taken out again. A zero nothing comes after is kept, since that is the loan paid off.
 */
function unrecordedZerosDropped(points) {
	const lastOwing = points.reduce((last, point) => (point.amount !== 0 && (!last || point.date.isAfter(last)) ? point.date : last), null);
	return points.filter((point) => point.amount !== 0 || !lastOwing || !point.date.isBefore(lastOwing));
}

/**
 * The account's balance over time, ready to draw, with a loan category's recorded balance beside it
 * when there is one: `{ granularity, labels, series: [{ key, label, values }] }`, `values` lined up
 * with `labels` and null where a series has nothing for that point. Each point is the last balance
 * read that day or month. A history of a few months or less is drawn by day; anything longer, or
 * one with a loan category's monthly balances in it, by month.
 *
 * A debt is drawn as the amount owed, positive.
 */
export function balanceHistory(account, { loanCategories = [] } = {}) {
	const balances = (account.balances || []).filter((balance) => balance.created_at && balance.current !== null && balance.current !== undefined);
	const categorySeries = loanCategories.map((category) => ({
		category,
		points: unrecordedZerosDropped((category.getBudgetsInOrder ? category.getBudgetsInOrder() : [])
			.filter((budget) => budget.fund_total !== '' && budget.fund_total !== null && budget.fund_total !== undefined && budget.date)
			.map((budget) => ({ date: budget.date, amount: Number(budget.fund_total) }))),
	})).filter((series) => series.points.length > 0);

	const dates = balances.map((balance) => balance.created_at);
	categorySeries.forEach((series) => series.points.forEach((point) => dates.push(point.date)));
	if(!dates.length) {
		return { granularity: null, labels: [], series: [] };
	}
	const earliest = moment.min(dates);
	const latest = moment.max(dates);
	const monthly = categorySeries.length > 0 || latest.diff(earliest, 'days') > DAILY_SPAN_DAYS;
	const keyFormat = monthly ? 'YYYY-MM' : 'YYYY-MM-DD';
	const labelFormat = monthly ? 'MMM YYYY' : 'MMM D';

	const keys = new Map();
	const lastBy = (points) => {
		const byKey = new Map();
		for(const point of points) {
			const key = point.date.format(keyFormat);
			const held = byKey.get(key);
			if(!held || !point.date.isBefore(held.date)) {
				byKey.set(key, point);
			}
			if(!keys.has(key)) {
				keys.set(key, point.date.format(labelFormat));
			}
		}
		return byKey;
	};

	const accountPoints = lastBy(balances.map((balance) => ({ date: balance.created_at, amount: plottedAmount(account, balance) })));
	const categoryPoints = categorySeries.map((series) => ({ category: series.category, byKey: lastBy(series.points) }));

	/**
	 * Every day or month from the first to the last, not just those with a reading, so the axis is
	 * proportional to time; a gap is a null, which the line spans.
	 */
	const unit = monthly ? 'month' : 'day';
	const sortedKeys = [];
	for(const cursor = earliest.clone().startOf(unit); !cursor.isAfter(latest, unit); cursor.add(1, unit)) {
		const key = cursor.format(keyFormat);
		sortedKeys.push(key);
		if(!keys.has(key)) {
			keys.set(key, cursor.format(labelFormat));
		}
	}
	const series = [];
	if(accountPoints.size) {
		series.push({
			key: 'account',
			label: account.getDisplayName(),
			values: sortedKeys.map((key) => (accountPoints.has(key) ? accountPoints.get(key).amount : null)),
		});
	}
	for(const { category, byKey } of categoryPoints) {
		series.push({
			key: `category-${category.id}`,
			label: `${category.name} (budget)`,
			values: sortedKeys.map((key) => (byKey.has(key) ? byKey.get(key).amount : null)),
		});
	}
	return {
		granularity: monthly ? 'month' : 'day',
		labels: sortedKeys.map((key) => keys.get(key)),
		series,
	};
}

/** Whether a history has enough in it to be worth a line: two points in any one series. */
export function historyIsDrawable(history) {
	return history.series.some((series) => series.values.filter((value) => value !== null).length >= 2);
}

/**
 * How many more monthly payments of `payment` clear `owed` at `annualRate` percent, interest added
 * monthly: the standard amortization count, rounded up. Null when it never would (the payment does
 * not cover a month's interest) or when something it needs is missing.
 */
export function monthsToPayOff(owed, annualRate, payment) {
	owed = Number(owed);
	payment = Number(payment);
	const rate = (Number(annualRate) || 0) / 100 / 12;
	if(owed <= 0) {
		return 0;
	}
	if(!(owed > 0) || !(payment > 0)) {
		return null;
	}
	if(rate === 0) {
		return Math.ceil(owed / payment);
	}
	if(payment <= owed * rate) {
		return null;
	}
	return Math.ceil(-Math.log(1 - (rate * owed) / payment) / Math.log(1 + rate));
}

/**
 * A loan category's terms as of `date`, as recorded in the budget, and when `owed` would be paid off
 * at them: `{ category, rate, minimumPayment, payment, months, payoffDate, missing }`. `payment` is
 * the larger of the minimum and `recentPayment` (what the balances say has been paid lately), since
 * that is what it is being paid at. Amounts in cents, the rate in percent a year.
 *
 * `owed` null (the account has no balance) falls back to the category's last recorded balance.
 * There is no payoff without both a payment and a rate: `missing` is 'payment' or 'rate' then, and
 * a rate is never taken to be 0% for want of one. With both, `months` null means never at that payment.
 */
export function loanTerms(category, owed, date, recentPayment = null) {
	const rate = parseFloat(category.lastKnownInterestAmountBeforeDate(date));
	const minimum = Number(category.lastKnownMinimumPaymentBeforeDate(date)) || null;
	const payment = Math.max(minimum || 0, Number(recentPayment) || 0) || null;
	if(owed === null || owed === undefined) {
		const recorded = category.lastKnownBalanceBeforeDate ? category.lastKnownBalanceBeforeDate(date) : null;
		owed = recorded === null || recorded === '' ? null : Number(recorded);
	}
	const hasRate = Number.isFinite(rate);
	let missing = null;
	if(owed === null) {
		missing = 'balance';
	} else if(!payment && owed > 0) {
		missing = 'payment';
	} else if(!hasRate && owed > 0) {
		missing = 'rate';
	}
	const months = missing ? null : monthsToPayOff(owed, rate, payment);
	return {
		category,
		rate: hasRate ? rate : null,
		minimumPayment: minimum,
		payment,
		months,
		missing,
		payoffDate: months === null ? null : date.clone().add(months, 'months').startOf('month'),
	};
}

/**
 * The y axis's bounds for a history: the values' range padded a tenth either side, and widened to at
 * least MIN_AXIS_SPAN around its middle, so a cent's change is not drawn as a cliff and a single
 * point sits mid-chart rather than on the floor. `{ min, max, span }` in cents, or null with no values.
 */
const MIN_AXIS_SPAN = 2000;

export function axisBounds(history) {
	const values = history.series.flatMap((series) => series.values).filter((value) => value !== null);
	if(!values.length) {
		return null;
	}
	let min = Math.min(...values);
	let max = Math.max(...values);
	const pad = (max - min) * 0.1;
	min -= pad;
	max += pad;
	const span = Math.max(max - min, MIN_AXIS_SPAN, Math.abs((max + min) / 2) * 0.02);
	const middle = (max + min) / 2;
	min = middle - span / 2;
	max = middle + span / 2;
	/** A balance that never went below zero is not drawn as if it did. */
	if(Math.min(...values) >= 0 && min < 0) {
		max -= min;
		min = 0;
	}
	return { min, max, span: max - min };
}

/**
 * An axis label for `cents` on an axis spanning `span` cents: thousands as "$12k" on a wide one,
 * whole dollars on a narrower one and cents on a narrow one, with the sign before the dollar sign.
 */
export function axisMoney(cents, span) {
	const sign = cents < 0 ? '-' : '';
	const dollars = Math.abs(cents) / 100;
	if(span >= 1000000) {
		return `${sign}$${Math.round(dollars / 1000).toLocaleString('en-US')}k`;
	}
	if(span >= 100000 && dollars >= 1000) {
		return `${sign}$${(dollars / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}k`;
	}
	if(span >= 1000) {
		return `${sign}$${Math.round(dollars).toLocaleString('en-US')}`;
	}
	return `${sign}$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Which of `count` labels to show, at most `limit`, spread evenly and always taking the first and the
 * last, so the axis says where the line ends.
 */
export function labelledTicks(count, limit) {
	if(count <= limit) {
		return new Set([...Array(count).keys()]);
	}
	const shown = new Set();
	for(let index = 0; index < limit; index++) {
		shown.add(Math.round((index * (count - 1)) / (limit - 1)));
	}
	return shown;
}
